import { useAppSelector } from 'hooks/redux';
import { Map as ImmutableMap } from 'immutable';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import DeleteShareIcon from '@mui/icons-material/CallMissedOutgoing';
import SwitchDatasetIcon from '@mui/icons-material/KeyboardTab';

import { authActions, authSelectors, authTypes } from 'app-components/flux/auth';
import { actionHelpers } from 'app-components/flux/core';
import { datasetsSelectors, datasetsUtils } from 'app-components/flux/datasets';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { sharesActions, type sharesTypes } from 'app-components/flux/shares';
import { getLogger } from 'app-components/utils/core';

import type { Dataset } from 'app-components/flux/datasets/datasetsTypes';
import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';

const log = getLogger('SharedDatasetMenu');

const SharedDatasetMenu = ({ dataset, shares }: { dataset: Dataset; shares: sharesTypes.Share[] | undefined }) => {
  const dispatch = useDispatch();

  const activeDatasetId = useAppSelector(authSelectors.getDatasetId);
  const datasetIsActive = dataset.id === activeDatasetId;

  // get another dataset to switch to, it must have entitlements,
  // if there are none assume the user does not have a subscription
  const firstValidDataset = useAppSelector((state) =>
    datasetsSelectors
      .getDatasets(state)
      ?.filter((dataset: Dataset) => dataset.id !== activeDatasetId && dataset.entitlements?.length)
      .valueSeq()
      .first(),
  );

  const hasActiveEntitlement = useMemo(() => datasetsUtils.hasActiveEntitlement(dataset), [dataset]);

  // on a dataset not owned by user, there is only one share (the users share)
  const share = shares && shares[0];

  const handleLeaveShare = () => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-share',
          type: CONFIRMATION_DIALOG_TYPE,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Leave this space',
              content: 'After leaving, you will no longer have access to this space.',
              confirmLabel: 'Continue',
              denyLabel: 'Cancel',
              confirmAction: () => {
                const deleteSharePromise = actionHelpers.bindPromiseAction(dispatch, sharesActions.deleteShare);
                deleteSharePromise(share)
                  .then(
                    () => {
                      if (datasetIsActive) {
                        if (firstValidDataset) {
                          dispatch(
                            authActions.authSelectDataset(
                              authTypes.AuthSelectDatasetPayload({ dataset: firstValidDataset }),
                            ),
                          );
                        } else {
                          //no sub
                          dispatch(
                            authActions.authLogout(
                              { reason: 'left only authorized space, no sub' },
                              { context: 'auth dialog: logout manually' },
                            ),
                          );
                        }
                      }
                    },
                    () => {
                      log.error('Deleting Share Failed');
                      // TODO: What to do in case of failure?
                    },
                  )
                  .catch((e) => assert(false, e));
              },
            }),
          ),
        }),
      ),
    );
  };

  const handleSelectDataset = () => {
    dispatch(authActions.authSelectDataset(authTypes.AuthSelectDatasetPayload({ dataset })));
  };

  return (
    <QDropdownMenu
      id={`dataset-menu-button-${dataset.id}`}
      aria-label='Space options'
      iconButtonVariant='plain'
      iconButtonSize='large'
      disabled={!hasActiveEntitlement}
    >
      <QMenuItem
        variant='action'
        label='Switch to this space'
        disabled={dataset.id === activeDatasetId}
        startIcon={<SwitchDatasetIcon fontSize='small' />}
        onClick={handleSelectDataset}
      />
      {share ? <QMenuDivider /> : null}
      {share ? (
        <QMenuItem
          variant='action'
          label='Leave this space'
          destructive
          startIcon={<DeleteShareIcon fontSize='small' />}
          onClick={handleLeaveShare}
        />
      ) : null}
    </QDropdownMenu>
  );
};

export default SharedDatasetMenu;
